import { Component, Input, OnInit, inject } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ListItem } from '../../../../models/api/core/list/list-item';
import { AlertsService } from '../../../../services/alerts/alerts.service';
import { BaseComponent } from '../../../base/base.component';
import { SelectOptionComponent } from './select-option.component';

@Component({
  selector: 'app-cause-code-option',
  imports: [SelectOptionComponent],
  template: `
    <app-select-option
      [label]="label"
      [control]="control"
      [showList]="false"
      [items]="claimTypes"
      [multiple]="true"
      [filter]="true"
      [showHeader]="true"
      [optionValue]="optionValue"
    />
  `,
  styles: ``,
})
export class CauseCodeOptionComponent extends BaseComponent implements OnInit {
  private alerts = inject(AlertsService);

  @Input()
  control: FormControl<any[]>;

  @Input()
  lobControl: FormControl<string[]>;

  @Input()
  codeType: CauseCodeType;

  @Input()
  label: string;

  claimTypes: ListItem[];

  readonly optionValue = 'value';

  private pendingValues: any[] | null = null;

  async ngOnInit() {
    this.control.addValidators([Validators.required]);

    // Preserve any values set by the edit-mode mapper before this component initializes
    const existing = this.control.value;
    if (existing?.length > 0) {
      this.pendingValues = existing;
    } else if (existing == null) {
      this.control.setValue([]);
    }
    this.control.updateValueAndValidity({ emitEvent: false });

    if (this.lobControl) {
      this.dispose.subs = this.lobControl.valueChanges.subscribe(() => {
        this.control.setValue([]);
        this.pendingValues = null;
        this.loadCodes();
      });
    }

    await this.loadCodes();
  }

  private async loadCodes() {
    const alert = this.alerts.currentAlert();
    const contractNumber = alert?.contracts?.[0]?.contNum ?? null;

    if (!contractNumber) {
      this.claimTypes = [];
      return;
    }

    const lobs = this.lobControl?.value ?? alert?.linesOfBusiness ?? [];
    const rawCodes = await this.api.newAlert.getConditionCodes({
      contractNumber: contractNumber,
      codeType: this.codeType,
      lobs: lobs,
    });

    const baseCodes = this.makeValuesUnique(rawCodes);
    this.alerts.loadedConditionCodes = baseCodes;
    // UI-only decoration: keep stored values untouched (XML/save payload uses values, not descriptions)
    this.claimTypes = this.decorateUiDescriptions(baseCodes);

    if (this.pendingValues?.length > 0) {
      const restored = this.pendingValues.map((pv) => {
        const uniqueValue = typeof pv === 'string' ? pv : (pv.value ?? pv);
        return (
          this.claimTypes.find((ct) => ct.value === uniqueValue) ?? {
            value: uniqueValue,
            description: uniqueValue,
          }
        );
      });
      this.control.setValue(restored.map((item) => item.value));
      this.pendingValues = null;
    }
  }

  private decorateUiDescriptions(items: ListItem[]): ListItem[] {
    return (items ?? []).map((item) => {
      const lob = item.lineOfBusiness?.trim();
      if (!lob) {
        return item;
      }

      const suffix = ` (${lob})`;
      const baseDescription = item.description ?? '';
      const description = baseDescription.endsWith(suffix)
        ? baseDescription
        : `${baseDescription}${suffix}`;

      return {
        ...item,
        description,
      };
    });
  }

  private makeValuesUnique(items: ListItem[]): ListItem[] {
    const valueCounts = new Map<string, number>();
    return items.map((item) => {
      const count = valueCounts.get(item.value) || 0;
      valueCounts.set(item.value, count + 1);
      const uniqueValue = count === 0 ? item.value : `${item.value}_${count}`;
      return {
        value: uniqueValue,
        description: item.description,
        contNum: item.contNum,
        lineOfBusiness: item.lineOfBusiness,
      };
    });
  }
}

export type CauseCodeType = 'Cause' | 'Nature' | 'BodyPart';
